
import React, { useState } from "react";
import { DaToolbar, DaToolbarButton, Search, StyledInputBase } from "./Toolbar.styles";
import InputAdornment from '@mui/material/InputAdornment';
import Box from "@mui/material/Box";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { FilterDropdown, FilterOption } from "./FilterDropdown";

export interface ListViewToolbarProps<T = string> {
    onClickNew: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onClickEdit: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onClickDelete: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onClickView?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onClickReset: (e: React.MouseEvent<HTMLButtonElement>) => void;
    execSearch: (term: string) => void;
    canView?: boolean;
    canAdd?: boolean;
    canUpdate?: boolean;
    canDelete?: boolean;
    canSearch?:boolean;
    showNewButton?: boolean;
    filterOptions?: FilterOption<T>[];
    selectedFilters?: T[];
    onFilterChange?: (selectedValues: T[]) => void;
    filterLabel?: string;
}

export default ListViewToolbar;

export function ListViewToolbar<T extends string = string>(props: ListViewToolbarProps<T>): JSX.Element {
    const {
        onClickNew,
        onClickEdit,
        onClickDelete,
        onClickView,
        onClickReset,
        execSearch,
        canView = false,
        canAdd = false,
        canUpdate = false,
        canDelete = false,
        canSearch = true,
        showNewButton = true,
        filterOptions,
        selectedFilters = [],
        onFilterChange,
        filterLabel = "Filter",
    } = props;

    const [searchTerm, setSearchTerm] = useState("");

    // Handlers for search
    const onBrokerSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            execSearch(searchTerm);
        }
    };
    const onBrokerSearchClick = (e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
        execSearch(searchTerm);
    };

    return (
        <DaToolbar
            variant="dense"
            sx={{
                flexDirection: { xs: 'column', sm: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', sm: 'flex-start', md: 'center' },
                gap: { xs: 0.5, sm: 0.5, md: 0 },
                px: { xs: 0.5, sm: 1, md: 2 },
                minHeight: 'auto',
                marginBottom: 0, // Remove margin to eliminate white space
            }}
        >
            {/* Desktop/tablet: action buttons and search bar in a row */}
            <Box
                sx={{
                    width: '100%',
                    mt: { xs: 0, sm: 0, md: 0 },
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}
            >
                {/* Action buttons left (icon+label for desktop) */}
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                    {showNewButton && (
                        <DaToolbarButton
                            startIcon={<AddIcon />}
                            disabled={!canAdd}
                            onClick={onClickNew}
                        >
                            New
                        </DaToolbarButton>
                    )}
                    {onClickView && (
                        <DaToolbarButton
                            startIcon={<VisibilityIcon />}
                            disabled={!canView}
                            onClick={onClickView}
                        >
                            View
                        </DaToolbarButton>
                    )}
                    <DaToolbarButton
                        startIcon={<EditIcon />}
                        disabled={!canUpdate}
                        onClick={onClickEdit}
                    >
                        Edit
                    </DaToolbarButton>
                    <DaToolbarButton
                        startIcon={<DeleteIcon />}
                        disabled={!canDelete}
                        onClick={onClickDelete}
                    >
                        Delete
                    </DaToolbarButton>
                    <DaToolbarButton
                        startIcon={<RestartAltIcon />}
                        onClick={e => {
                            setSearchTerm("");
                            onClickReset(e);
                        }}
                    >
                        Reset
                    </DaToolbarButton>
                </Box>
                {/* Search bar right */}
                {canSearch && <Box>
                    <Search>
                        <StyledInputBase
                            placeholder="Search..."
                            inputProps={{ 'aria-label': 'search' }}
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyUp={onBrokerSearchKeyUp}
                            endAdornment={
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="search"
                                        edge="end"
                                        onClick={onBrokerSearchClick}
                                        size="small"
                                    >
                                        <SearchIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            }
                        />
                    </Search>
                </Box>}
            </Box>

            {/* Mobile: icon-only action buttons in one row */}
            <Box
                sx={{
                    width: '100%',
                    display: { xs: 'flex', md: 'none' },
                    flexDirection: 'row',
                    gap: 1,
                    mb: 1,
                    overflowX: 'auto',
                    pl: 0,
                    pr: 0,
                }}
            >
                {showNewButton && (
                    <DaToolbarButton
                        disabled={!canAdd}
                        onClick={onClickNew}
                        sx={{ minWidth: 36, maxWidth: 36, minHeight: 36, maxHeight: 36, p: 0, m: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        <AddIcon />
                    </DaToolbarButton>
                )}
                {onClickView && (
                    <DaToolbarButton
                        disabled={!canView}
                        onClick={onClickView}
                        sx={{ minWidth: 36, maxWidth: 36, minHeight: 36, maxHeight: 36, p: 0, m: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                    >
                        <VisibilityIcon />
                    </DaToolbarButton>
                )}
                <DaToolbarButton
                    disabled={!canUpdate}
                    onClick={onClickEdit}
                    sx={{ minWidth: 36, maxWidth: 36, minHeight: 36, maxHeight: 36, p: 0, m: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <EditIcon />
                </DaToolbarButton>
                <DaToolbarButton
                    disabled={!canDelete}
                    onClick={onClickDelete}
                    sx={{ minWidth: 36, maxWidth: 36, minHeight: 36, maxHeight: 36, p: 0, m: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <DeleteIcon />
                </DaToolbarButton>
                <DaToolbarButton
                    onClick={e => {
                        setSearchTerm("");
                        onClickReset(e);
                    }}
                    sx={{ minWidth: 36, maxWidth: 36, minHeight: 36, maxHeight: 36, p: 0, m: 0, display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <RestartAltIcon />
                </DaToolbarButton>
            </Box>

            {/* Mobile search bar (only visible on mobile) */}
            <Box
                sx={{
                    width: '100%',
                    mt: 0.5,
                    display: { xs: 'flex', md: 'none' },
                    justifyContent: 'flex-start',
                    pl: 0,
                    pr: 0,
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        background: '#fff',
                        borderRadius: 2,
                        border: '1px solid #b0b0b0',
                        px: 1,
                        height: 36,
                    }}
                >
                    <SearchIcon sx={{ color: '#888', fontSize: 22, mr: 1 }} />
                    <input
                        type="text"
                        placeholder="Search..."
                        aria-label="search"
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        onKeyUp={onBrokerSearchKeyUp}
                        style={{
                            border: 'none',
                            outline: 'none',
                            background: 'transparent',
                            fontSize: 16,
                            width: '100%',
                            height: 32,
                            padding: 0,
                        }}
                    />
                </Box>
            </Box>
        </DaToolbar>
    );
}
